import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard, CheckCircle, XCircle, Clock, DollarSign } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPayments = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, completed: 0, pending: 0, revenue: 0 });

  const fetchTransactions = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API_URL}/api/payments/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data);
      
      // Calculate stats
      const completed = response.data.filter(t => t.payment_status === 'paid').length;
      const pending = response.data.filter(t => t.payment_status === 'pending').length;
      const revenue = response.data
        .filter(t => t.payment_status === 'paid')
        .reduce((sum, t) => sum + (t.amount || 0), 0);
      
      setStats({
        total: response.data.length,
        completed,
        pending,
        revenue
      });
    } catch (error) {
      toast.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      default:
        return <XCircle className="w-5 h-5 text-red-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-green-500/20 text-green-500',
      pending: 'bg-yellow-500/20 text-yellow-500',
      failed: 'bg-red-500/20 text-red-500',
    };
    return styles[status] || styles.failed;
  };

  return (
    <div data-testid="admin-payments">
      <h1 className="font-['Oswald'] text-3xl text-[#F2EDE6] mb-8">PAYMENTS</h1>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-[#141414] border border-[#2A2A2A] p-6">
          <CreditCard className="w-6 h-6 text-[#E8651A] mb-2" />
          <p className="font-['Oswald'] text-3xl text-[#F2EDE6]">{stats.total}</p>
          <p className="text-[#A19D98] text-sm">Total Transactions</p>
        </div>
        <div className="bg-[#141414] border border-[#2A2A2A] p-6">
          <CheckCircle className="w-6 h-6 text-green-500 mb-2" />
          <p className="font-['Oswald'] text-3xl text-[#F2EDE6]">{stats.completed}</p>
          <p className="text-[#A19D98] text-sm">Completed</p>
        </div>
        <div className="bg-[#141414] border border-[#2A2A2A] p-6">
          <Clock className="w-6 h-6 text-yellow-500 mb-2" />
          <p className="font-['Oswald'] text-3xl text-[#F2EDE6]">{stats.pending}</p>
          <p className="text-[#A19D98] text-sm">Pending</p>
        </div>
        <div className="bg-[#141414] border border-[#2A2A2A] p-6">
          <DollarSign className="w-6 h-6 text-green-500 mb-2" />
          <p className="font-['Oswald'] text-3xl text-[#F2EDE6]">${stats.revenue.toLocaleString()}</p>
          <p className="text-[#A19D98] text-sm">Total Revenue</p>
        </div>
      </div>

      {loading ? (
        <p className="text-[#A19D98]">Loading...</p>
      ) : transactions.length === 0 ? (
        <div className="bg-[#141414] border border-[#2A2A2A] p-8 text-center">
          <CreditCard className="w-12 h-12 text-[#2A2A2A] mx-auto mb-4" />
          <p className="text-[#A19D98]">No payment transactions yet.</p>
          <p className="text-[#A19D98] text-sm mt-2">Transactions will appear here when customers pay for rallies.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Rally</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((transaction) => (
                <tr key={transaction.id} data-testid={`payment-row-${transaction.id}`}>
                  <td className="text-[#F2EDE6]">{transaction.rally_name}</td>
                  <td className="text-[#F2EDE6]">
                    ${transaction.amount?.toLocaleString()} {transaction.currency?.toUpperCase()}
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(transaction.payment_status)}
                      <span className={`px-2 py-1 text-xs font-['Oswald'] tracking-wider rounded ${getStatusBadge(transaction.payment_status)}`}>
                        {transaction.payment_status?.toUpperCase()}
                      </span>
                    </div>
                  </td>
                  <td className="text-[#A19D98]">
                    {new Date(transaction.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminPayments;
